// src/pages/CallEndedPage.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { mentoringApi } from "../axiosInstance";

function formatDuration(sec) {
  if (!Number.isFinite(sec) || sec < 0) return "—";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

function safeGetLocalStorage(key) {
  try {
    return window?.localStorage?.getItem(key) || "";
  } catch {
    return "";
  }
}

function detectLang(location) {
  const search = location?.search || "";
  const sp = new URLSearchParams(search);

  const q = (sp.get("lang") || sp.get("language") || sp.get("locale") || "").trim();

  const stored =
    safeGetLocalStorage("lang") ||
    safeGetLocalStorage("i18nextLng") ||
    safeGetLocalStorage("language");

  const nav = (typeof navigator !== "undefined" && navigator.language) || "";

  const raw = String(q || stored || nav || "de").toLowerCase();
  if (raw.startsWith("en")) return "en";
  return "de";
}

function dashboardPathForRole(role) {
  const r = String(role || "").toLowerCase();

  if (r === "mentor") return "/mentor-dashboard";
  if (r === "mentee") return "/mentee-dashboard";

  // falls du später HR/Superadmin auch über diese Seite führen willst
  if (r === "hr" || r === "hr_admin" || r === "hr_user") return "/hr";
  if (r === "superadmin") return "/superadmin";

  return "/";
}

const I18N = {
  de: {
    badge: "MENVIO Call-System",
    title: "Call beendet",
    subtitle: "Danke! Dein Gespräch wurde beendet.",
    duration: "Dauer",
    room: "Raum",
    qualityTitle: "Wie war die Call-Qualität insgesamt?",
    qualityHint: "Kurzes Feedback hilft, dein Setup und unsere Infrastruktur zu verbessern.",
    statusPrefix: "Status:",
    statusNone: "Noch keine Bewertung",
    statusCritical: "Kritisch",
    statusOk: "Okay",
    statusGood: "Gut",
    statusVeryGood: "Sehr gut",
    issueAudio: "Audio-Probleme",
    issueVideo: "Video-Probleme",
    issueLags: "Lags/Abbrüche",
    issueEcho: "Echo/Hall",
    commentPlaceholder: "Was war auffällig? (optional)",
    saveHint: "Speichern machen wir im nächsten Schritt (Backend).",
    backDashboard: "Zurück zum Dashboard",
    newCall: "Neuen Call starten",
    submitting: "Speichere Feedback...",
    submitError: "Feedback konnte nicht gespeichert werden.",
  },
  en: {
    badge: "MENVIO Call System",
    title: "Call ended",
    subtitle: "Thanks! Your conversation has ended.",
    duration: "Duration",
    room: "Room",
    qualityTitle: "How was the overall call quality?",
    qualityHint: "Quick feedback helps improve your setup and our infrastructure.",
    statusPrefix: "Status:",
    statusNone: "No rating yet",
    statusCritical: "Critical",
    statusOk: "OK",
    statusGood: "Good",
    statusVeryGood: "Very good",
    issueAudio: "Audio issues",
    issueVideo: "Video issues",
    issueLags: "Lags / disconnects",
    issueEcho: "Echo / reverb",
    commentPlaceholder: "Anything noticeable? (optional)",
    saveHint: "Saving comes next (backend).",
    backDashboard: "Back to dashboard",
    newCall: "Start new call",
    submitting: "Saving feedback...",
    submitError: "Feedback could not be saved.",
  },
};

export default function CallEndedPage() {
  const nav = useNavigate();
  const location = useLocation();
  const { state } = location;

  // State-Caching via sessionStorage: useRef verliert bei Remount seinen Wert,
  // sessionStorage überlebt Remounts und persistiert bis Tab geschlossen wird.
  // Key-Strategie: callend:<roomName>, damit paralleles Call-Feedback möglich wäre
  const STORAGE_KEY_PREFIX = 'callend:';

  // Schreiben: wenn location.state vorhanden und roomName extrahierbar, cachen
  if (state && state.roomName) {
    try {
      sessionStorage.setItem(STORAGE_KEY_PREFIX + state.roomName, JSON.stringify(state));
      // Zusätzlich: last-key speichern für Zugriff ohne bekannten roomName
      sessionStorage.setItem('callend:_lastKey', STORAGE_KEY_PREFIX + state.roomName);
    } catch (e) {
      console.warn('[CALLEND] sessionStorage write failed:', e);
    }
  }

  // Lesen: location.state bevorzugt, sonst aus sessionStorage via lastKey
  const frozenState = useMemo(() => {
    if (state) return state;
    try {
      const lastKey = sessionStorage.getItem('callend:_lastKey');
      if (!lastKey) return null;
      const cached = sessionStorage.getItem(lastKey);
      return cached ? JSON.parse(cached) : null;
    } catch (e) {
      console.warn('[CALLEND] sessionStorage read failed:', e);
      return null;
    }
  }, [state]);

  const lang = useMemo(() => detectLang(location), [location.search]);
  const t = I18N[lang] || I18N.de;

  // Werte aus frozenState (stabil) lesen, nicht aus location.state (kann null werden bei Remount)
  // Backwards-compat: auch state.callMeta.* akzeptieren, falls alte Call-Komponenten noch aktiv
  const callState = frozenState || {};
  const legacyMeta = frozenState?.callMeta || {};
  const roomName = callState.roomName ?? legacyMeta.room ?? null;
  const durationSec = callState.durationSec ?? legacyMeta.durationSec ?? null;
  const matchingId = callState.matchingId ?? legacyMeta.matchingId ?? null;
  const peerUserId = callState.peerUserId ?? legacyMeta.peerUserId ?? null;
  const peerRole = callState.peerRole ?? legacyMeta.peerRole ?? null;
  const hadPartner = callState.hadPartner ?? legacyMeta.hadPartner ?? false;
  const connectionQuality = callState.connectionQuality ?? legacyMeta.connectionQuality ?? null;

  const durationLabel = useMemo(() => formatDuration(durationSec), [durationSec]);

  // Rolle “sauber & dauerhaft”: aus Session ziehen (whoami), nicht aus Redirect-State
  const [sessionRole, setSessionRole] = useState("");

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const r = await fetch("/mentoring/whoami", { credentials: "include" });
        const j = await r.json().catch(() => ({}));
        const role = j?.user?.role || j?.role || "";
        if (alive) setSessionRole(role);
      } catch {
        // bewusst leer: Fallback greift dann unten
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  // UI (noch ohne Backend)
  const [rating, setRating] = useState(null); // 1..5
  const [issues, setIssues] = useState([]);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const toggleIssue = (key) => {
    setIssues((prev) => (prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]));
  };

  const ratingLabel =
    rating === null
      ? t.statusNone
      : rating <= 2
      ? t.statusCritical
      : rating === 3
      ? t.statusOk
      : rating === 4
      ? t.statusGood
      : t.statusVeryGood;

  const ISSUE_OPTIONS = useMemo(
    () => [
      { key: "audio", label: t.issueAudio },
      { key: "video", label: t.issueVideo },
      { key: "lags", label: t.issueLags },
      { key: "echo", label: t.issueEcho },
    ],
    [t]
  );

  const backTarget = useMemo(() => {
    // 1) Session-Rolle (sicher)
    if (sessionRole) return dashboardPathForRole(sessionRole);

    // 2) Fallback: Redirect-State (falls vorhanden)
    const metaRole = legacyMeta?.role || legacyMeta?.userRole || legacyMeta?.user?.role || "";
    if (metaRole) return dashboardPathForRole(metaRole);

    // 3) letzter Fallback
    return "/";
  }, [sessionRole, legacyMeta?.role, legacyMeta?.userRole, legacyMeta?.user?.role]);

  // Solo-Call-Skip: Wenn kein Partner im Call war, Feedback-Screen überspringen.
  // Guard: Nur triggern wenn frozenState vorliegt. Bei Remount ohne State nicht skippen.
  useEffect(() => {
    if (!frozenState) {
      return;
    }
    if (hadPartner === false && sessionRole) {
      nav(backTarget, { replace: true });
    }
  }, [frozenState, hadPartner, sessionRole, backTarget, nav]);

  // POST-Feedback. Läuft bei Klick auf Dashboard- oder Neuer-Call-Button.
  // Skip-Logik: Wenn alle Felder leer (kein Signal), kein Insert.
  async function submitFeedbackIfAny() {
    if (submitted) return true; // schon gesendet, direkt weiter
    const noSignal = rating === null && issues.length === 0 && !comment.trim();
    if (noSignal) {
      setSubmitted(true);
      return true;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      await mentoringApi.post("/mentoring/call-feedback", {
        room: roomName,
        durationSec: durationSec,
        rating: rating,
        issues: issues,
        comment: comment.trim(),
        matchingId: matchingId,
        toUserId: peerUserId,
        toRole: peerRole,
        meta: {
          connectionQuality: connectionQuality,
          hadPartner: hadPartner,
          endedReason: callState.reason || null,
        },
      });
      setSubmitted(true);
      // Cleanup: sessionStorage-Cache für diesen Raum entfernen
      try {
        if (roomName) {
          sessionStorage.removeItem(STORAGE_KEY_PREFIX + roomName);
        }
        sessionStorage.removeItem('callend:_lastKey');
      } catch (e) {
        console.warn('[CALLEND] sessionStorage cleanup failed:', e);
      }
      return true;
    } catch (e) {
      console.error("call-feedback submit failed:", e);
      setSubmitError(t.submitError);
      return false;
    } finally {
      setSubmitting(false);
    }
  }

  async function handleBackToDashboard() {
    const ok = await submitFeedbackIfAny();
    if (ok) nav(backTarget, { replace: true });
  }

  async function handleNewCall() {
    const ok = await submitFeedbackIfAny();
    if (ok) nav(-1);
  }

  return (
    <div
      className="min-h-screen w-full px-4 py-10 text-white"
      style={{
        background:
          "radial-gradient(1200px 600px at 50% -10%, rgba(52,211,153,0.14), transparent 60%), radial-gradient(900px 500px at 10% 20%, rgba(59,130,246,0.12), transparent 55%), radial-gradient(900px 500px at 90% 30%, rgba(168,85,247,0.12), transparent 55%), linear-gradient(180deg, #050608 0%, #030406 60%, #020305 100%)",
      }}
    >
      <div className="mx-auto w-full max-w-3xl">
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <div className="h-10 w-10 rounded-2xl bg-white/10 backdrop-blur-md border border-white/10 shadow-lg flex items-center justify-center">
            <span className="text-lg">✓</span>
          </div>

          <div className="flex-1">
            <h2 className="m-0 text-xl font-semibold tracking-tight">{t.title}</h2>
            <div className="mt-0.5 text-sm text-white/70">{t.subtitle}</div>
          </div>

          <div className="hidden sm:flex items-center gap-2">
            <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/70 backdrop-blur">
              {t.badge}
            </div>
          </div>
        </div>

        {/* Meta Card */}
        <div className="mb-5 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-[0_20px_60px_rgba(0,0,0,0.45)]">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-5">
            <div className="rounded-xl border border-white/10 bg-black/20 px-4 py-3">
              <div className="text-[11px] uppercase tracking-wider text-white/60">{t.duration}</div>
              <div className="mt-1 text-lg font-semibold">{durationLabel}</div>
            </div>

            <div className="rounded-xl border border-white/10 bg-black/20 px-4 py-3">
              <div className="text-[11px] uppercase tracking-wider text-white/60">{t.room}</div>
              <div className="mt-1 text-sm text-white/90 break-words">{roomName || "—"}</div>
            </div>
          </div>
        </div>

        {/* Feedback Card */}
        <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-[0_20px_60px_rgba(0,0,0,0.45)]">
          <div className="p-5 sm:p-6">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="text-base font-semibold">{t.qualityTitle}</div>
                <div className="mt-1 text-sm text-white/65">{t.qualityHint}</div>
              </div>

              <div className="shrink-0 rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-xs text-white/70">
                {t.statusPrefix}{" "}
                <span className="text-white/90 font-semibold">{ratingLabel}</span>
              </div>
            </div>

            {/* Rating */}
            <div className="flex flex-wrap gap-2 mb-4">
              {[1, 2, 3, 4, 5].map((n) => {
                const active = rating === n;
                return (
                  <button
                    key={n}
                    onClick={() => setRating(n)}
                    className={[
                      "h-11 w-11 rounded-xl font-semibold transition",
                      "border backdrop-blur",
                      active
                        ? "bg-emerald-500/16 border-emerald-300/35 text-emerald-100 shadow-[0_0_0_4px_rgba(52,211,153,0.10)]"
                        : "bg-white/5 border-white/12 text-white/85 hover:bg-white/10 hover:border-white/20",
                      "focus:outline-none focus:ring-2 focus:ring-emerald-300/30 focus:ring-offset-0",
                    ].join(" ")}
                    aria-label={`${t.statusPrefix} ${n}`}
                  >
                    {n}
                  </button>
                );
              })}
            </div>

            {/* Issue chips */}
            <div className="flex flex-wrap gap-2 mb-4">
              {ISSUE_OPTIONS.map((o) => {
                const active = issues.includes(o.key);
                return (
                  <button
                    key={o.key}
                    onClick={() => toggleIssue(o.key)}
                    className={[
                      "px-3 py-2 rounded-full text-sm transition border backdrop-blur",
                      active
                        ? "bg-emerald-500/12 border-emerald-300/30 text-emerald-100"
                        : "bg-white/5 border-white/12 text-white/80 hover:bg-white/10 hover:border-white/20",
                      "focus:outline-none focus:ring-2 focus:ring-emerald-300/25 focus:ring-offset-0",
                    ].join(" ")}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>

            {/* Comment */}
            <div className="rounded-2xl border border-white/10 bg-black/20 p-3">
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={4}
                placeholder={t.commentPlaceholder}
                className="w-full resize-none rounded-xl bg-transparent px-2 py-2 text-sm text-white/90 placeholder:text-white/40 focus:outline-none"
              />
            </div>
          </div>

          {/* Footer actions */}
          {submitError && (
            <div className="px-5 sm:px-6 pb-3 text-sm text-red-300/90">
              {submitError}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 px-5 sm:px-6 pb-6">
            <button
              onClick={handleBackToDashboard}
              disabled={submitting}
              className="h-11 rounded-xl border border-white/12 bg-white/5 hover:bg-white/10 hover:border-white/20 transition backdrop-blur text-white/90 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? t.submitting : t.backDashboard}
            </button>

            <button
              onClick={handleNewCall}
              disabled={submitting}
              className="h-11 rounded-xl border border-emerald-300/30 bg-emerald-500/14 hover:bg-emerald-500/20 transition backdrop-blur text-emerald-100 font-semibold shadow-[0_10px_30px_rgba(52,211,153,0.10)] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? t.submitting : t.newCall}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

